import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Save, Trash2, Eye, Send, Bold, Italic, Link as LinkIcon, AtSign, Sparkles, KeyRound, Target, Route, Link2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/app/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import { wallClockToUtcISO, utcISOToWallClock, tzLabel } from "@/lib/timezone";

const TRIGGER_OPTIONS = [
  { value: "time_after_signup",   label: "זמן אחרי הרשמה" },
  { value: "fixed_date",          label: "תאריך מוגדר" },
  { value: "after_email_opened",  label: "אחרי שמייל קודם נפתח" },
];

const DEFAULT_TEMPLATE = {
  name: "ברוך הבא #1",
  subject: "שלום {{name}}, הנה הצעד הבא שלך",
  html_body: `<p>שלום {{name}},</p>
<p>תודה שהשארת פרטים. זה הצעד הראשון לקחת שליטה על הכסף שלך.</p>
<p><strong>עכשיו זה הזמן לבדוק את הזכאות שלך לאיחוד הלוואות</strong> (2 דקות):</p>
<p><a href="{{affiliate_link}}" style="display:inline-block;background:#1c1917;color:#fff;padding:12px 24px;border-radius:999px;text-decoration:none;font-weight:bold">לבדיקת זכאות בחינם →</a></p>
<p style="color:#78716c;font-size:13px">הלינק אישי ודיסקרטי לחלוטין.</p>
<p>בהצלחה,<br/>צוות אמת או חובות</p>`,
  group: 1,
  generic_link: "",
  is_commercial: true,
  is_active: true,
  trigger_type: "time_after_signup",
  trigger_value: { offset_days: 0, send_hour_local: "09:00" },
};

export default function EmailEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  const [form, setForm] = useState(DEFAULT_TEMPLATE);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [otherTemplates, setOtherTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]); // available campaigns for scope selector
  const [leadLinkDestinations, setLeadLinkDestinations] = useState([]); // pre-defined lead-link routes
  const [shortLinks, setShortLinks] = useState([]); // user-created short links (/admin/short-links)
  const [leadLinkOpen, setLeadLinkOpen] = useState(false);
  const [namedLinkOpen, setNamedLinkOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const bodyRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [list, camps, dests, slinks] = await Promise.all([
          api.get("/admin/email/templates"),
          api.get("/campaigns"),
          api.get("/admin/email/lead-link-destinations"),
          api.get("/admin/short-links").catch(() => ({ data: [] })),
        ]);
        setOtherTemplates(list.data.filter((t) => t.id !== id));
        setCampaigns(camps.data || []);
        setLeadLinkDestinations(dests.data || []);
        setShortLinks((slinks.data || []).filter((s) => s.is_active));
        if (!isNew) {
          const { data } = await api.get(`/admin/email/templates/${id}`);
          setForm({
            ...DEFAULT_TEMPLATE,
            ...data,
            trigger_value: data.trigger_value || {},
            generic_link: data.generic_link || "",
            campaign_ids: data.campaign_ids || [],
          });
        }
      } catch (err) {
        console.error("Failed to load template", err);
        toast.error("טעינה נכשלה");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTV = (k, v) => setForm((f) => ({ ...f, trigger_value: { ...f.trigger_value, [k]: v } }));

  const insertAtCursor = (text) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const before = form.html_body.slice(0, start);
    const after = form.html_body.slice(end);
    const next = before + text + after;
    set("html_body", next);
    // Refocus + place cursor after insertion
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + text.length;
    });
  };

  const wrapSelection = (open, close) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = form.html_body.slice(start, end) || "טקסט";
    insertAtCursor(`${open}${selected}${close}`);
  };

  const save = async () => {
    if (form.group === 2 && !form.generic_link.trim()) {
      toast.error("קבוצה 2 דורשת LINK בסיסי");
      return;
    }
    if (form.trigger_type === "after_email_opened" && !form.trigger_value?.after_template_id) {
      toast.error("יש לבחור מייל קודם בטריגר");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (isNew) {
        const { data } = await api.post("/admin/email/templates", payload);
        toast.success("נשמר!");
        navigate(`/admin/emails/${data.id}`, { replace: true });
      } else {
        await api.put(`/admin/email/templates/${id}`, payload);
        toast.success("עודכן!");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "שמירה נכשלה");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("למחוק את ה-template? שליחות שכבר נשלחו לא יושפעו, אבל שליחות מתוזמנות יבוטלו.")) return;
    try {
      await api.delete(`/admin/email/templates/${id}`);
      toast.success("נמחק");
      navigate("/admin/emails");
    } catch (err) {
      toast.error("מחיקה נכשלה");
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) {
      toast.error("הזן אימייל לבדיקה");
      return;
    }
    if (isNew) {
      toast.error("שמור את ה-Template תחילה");
      return;
    }
    try {
      await api.post("/admin/email/test-send", { template_id: id, to_email: testEmail.trim() });
      toast.success("מייל בדיקה נשלח!");
      setTestOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "שליחת בדיקה נכשלה");
    }
  };

  const previewSubject = useMemo(() => {
    const raw = form.subject.replaceAll("{{name}}", "ישראלי ישראל");
    return form.is_commercial && !raw.includes("[פרסומת עבורך]") ? `${raw} [פרסומת עבורך]` : raw;
  }, [form.subject, form.is_commercial]);

  const previewHtml = useMemo(() => {
    let rendered = form.html_body
      .replaceAll("{{name}}", "ישראלי ישראל")
      .replaceAll("{{email}}", "test@example.com")
      .replaceAll("{{unsubscribe_link}}", "#preview-unsubscribe")
      .replaceAll("{{magic_link}}", "https://emetohovot.co.il/magic-login?token=mml_preview_token")
      .replaceAll("{{affiliate_link}}",
        form.group === 2
          ? `${form.generic_link || "https://example.com/?"}${form.generic_link.includes("?") ? "&" : "?"}sub1=fb_partner_a`
          : "https://example.com/eligibility-quiz?aff=fb_partner_a"
      );
    // Substitute {{link:KEY}} → mocked preview URL for the admin
    rendered = rendered.replace(/\{\{link:([a-zA-Z0-9_-]{1,32})\}\}/g,
      (_m, k) => `https://example.com/offer/${k}?sub1=fb_partner_a&sub2=lead_preview&sub4=tmpl_preview`);
    // Substitute {{short_link:SLUG}} with the actual long URL (preview-only mock
    // appending `?sub1..sub10` to show the admin what the lead will receive)
    rendered = rendered.replace(/\{\{short_link:([a-z0-9\-_]{2,40})\}\}/g, (_m, slug) => {
      const sl = shortLinks.find((x) => x.slug === slug);
      if (!sl) return "";
      const sep = sl.long_url.includes("?") ? "&" : "?";
      return `${sl.long_url}${sep}sub1=preview_aff&sub2=lead_preview&sub4=tmpl_preview&sub9=fb_preview`;
    });
    // Substitute lead_link:KEY placeholders with mock URLs (using a preview lead_id)
    for (const d of leadLinkDestinations) {
      rendered = rendered.replaceAll(d.placeholder, d.preview_url);
    }
    if (!form.is_commercial) return rendered;
    // Append a mock footer preview (actual footer is injected server-side)
    return rendered + `
<hr style="margin:32px 0 16px 0;border:none;border-top:1px solid #e7e5e4" />
<div dir="rtl" style="font-family:Arial,sans-serif;font-size:12px;color:#78716c;line-height:1.7;text-align:right">
  <p style="margin:0 0 8px 0"><strong>יאיר הלל</strong> · ע.מ / ח.פ: 300038155 · הכלנית 334 ברקת · טל׳: 0552705971</p>
  <p style="margin:0 0 8px 0">קיבלת הודעה זו לאחר שנרשמת מרצונך באחד מדפי הנחיתה של האתר.</p>
  <p style="margin:0"><a href="#preview-unsubscribe" style="color:#dc2626;font-weight:bold;text-decoration:underline">להסרה מרשימת התפוצה לחצו כאן</a></p>
</div>`;
  }, [form.html_body, form.group, form.generic_link, form.is_commercial, leadLinkDestinations, shortLinks]);

  if (loading) {
    return <AdminLayout title="טוען..."><div className="py-12 text-center text-stone-500">טוען...</div></AdminLayout>;
  }

  return (
    <AdminLayout title={isNew ? "Template חדש" : `עריכת: ${form.name}`}>
      {/* Top action bar */}
      <div className="bg-white rounded-2xl p-3 border border-stone-200 mb-5 flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => navigate("/admin/emails")} className="flex items-center gap-1.5 text-sm font-bold text-stone-600 hover:text-stone-900">
          <ArrowRight className="w-4 h-4 rotate-180" />
          חזרה
        </button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPreviewOpen(true)} data-testid="preview-btn" className="h-10 rounded-xl">
            <Eye className="w-4 h-4 me-2" />תצוגה מקדימה
          </Button>
          <Button variant="outline" onClick={() => setTestOpen(true)} disabled={isNew} data-testid="test-send-btn" className="h-10 rounded-xl">
            <Send className="w-4 h-4 me-2" />שלח לבדיקה
          </Button>
          {!isNew && (
            <Button variant="outline" onClick={remove} data-testid="delete-btn" className="h-10 rounded-xl text-red-600 hover:text-red-700 border-red-200">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          <Button onClick={save} disabled={saving} data-testid="save-btn" className="h-10 rounded-xl bg-brand-amber hover:bg-brand-amberHover text-white">
            <Save className="w-4 h-4 me-2" />{saving ? "שומר..." : "שמור"}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left: main editor */}
        <div className="lg:col-span-2 space-y-5">
          {/* Identity card */}
          <Card title="זיהוי">
            <Field label="שם פנימי (לא נשלח לנמען)">
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="form-name" className="h-11" />
            </Field>
            <Field label="נושא המייל">
              <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="form-subject" className="h-11" placeholder="שלום {{name}}, הנה ההצעה שלך…" />
            </Field>
            <p className="text-xs text-stone-500">השתמש ב-<code className="px-1 bg-stone-100 rounded">{`{{name}}`}</code> כדי להזריק את שם הנמען</p>
          </Card>

          {/* Body editor */}
          <Card title="תוכן המייל (HTML)">
            <div className="flex flex-wrap gap-1.5 mb-2 bg-stone-50 rounded-xl p-2 border border-stone-200">
              <ToolBtn onClick={() => wrapSelection("<strong>", "</strong>")} testId="tb-bold"><Bold className="w-3.5 h-3.5" />Bold</ToolBtn>
              <ToolBtn onClick={() => wrapSelection("<em>", "</em>")} testId="tb-italic"><Italic className="w-3.5 h-3.5" />Italic</ToolBtn>
              <ToolBtn onClick={() => insertAtCursor('<a href="{{affiliate_link}}">הטקסט שלי</a>')} testId="tb-link"><LinkIcon className="w-3.5 h-3.5" />לינק אפיליאייט</ToolBtn>
              <ToolBtn onClick={() => insertAtCursor("{{name}}")} testId="tb-name"><AtSign className="w-3.5 h-3.5" />{`{{name}}`}</ToolBtn>
              <ToolBtn onClick={() => insertAtCursor("{{affiliate_link}}")} testId="tb-affiliate"><Sparkles className="w-3.5 h-3.5" />{`{{affiliate_link}}`}</ToolBtn>
              <ToolBtn onClick={() => insertAtCursor('<a href="{{magic_link}}">כניסה לאזור החברים</a>')} testId="tb-magic-link"><KeyRound className="w-3.5 h-3.5" />לינק כניסה (Magic)</ToolBtn>
              <ToolBtn onClick={() => setLeadLinkOpen(true)} testId="tb-lead-link"><Route className="w-3.5 h-3.5" />לינק עם lead_id</ToolBtn>
              <ToolBtn onClick={() => setNamedLinkOpen(true)} testId="tb-named-link"><Link2 className="w-3.5 h-3.5" />לינק לפי שם (funnel)</ToolBtn>
              <ToolBtn onClick={() => insertAtCursor("<br/>")} testId="tb-br">↵ Break</ToolBtn>
            </div>
            <Textarea
              ref={bodyRef}
              value={form.html_body}
              onChange={(e) => set("html_body", e.target.value)}
              data-testid="form-body"
              rows={18}
              dir="ltr"
              className="font-mono text-xs"
            />
            <p className="text-xs text-stone-500 mt-2">
              משתנים זמינים: <code className="px-1 bg-stone-100 rounded">{`{{name}}`}</code>, <code className="px-1 bg-stone-100 rounded">{`{{email}}`}</code>, <code className="px-1 bg-stone-100 rounded">{`{{affiliate_link}}`}</code>, <code className="px-1 bg-stone-100 rounded">{`{{magic_link}}`}</code> (לינק כניסה אוטומטי לאזור החברים), <code className="px-1 bg-stone-100 rounded">{`{{lead_link:KEY}}`}</code> (לינק אישי ליעדים בסיסטם — השתמשו בכפתור &quot;לינק עם lead_id&quot;), <code className="px-1 bg-stone-100 rounded">{`{{link:KEY}}`}</code> (לינק אפיליאייט לפי שם המוגדר ב-funnel — השתמשו בכפתור &quot;לינק לפי שם&quot;).
              פיקסל פתיחה יתווסף אוטומטית בסוף המייל.
            </p>
          </Card>
        </div>

        {/* Right: meta panel */}
        <div className="space-y-5">
          <Card title="סוג המסר (רגולציה)">
            <div className={`p-3 rounded-xl border ${form.is_commercial ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-[800] text-sm mb-1">
                    {form.is_commercial ? "שיווקי / מסחרי" : "תפעולי / Transactional"}
                  </div>
                  <div className="text-xs text-stone-600 leading-[1.6]">
                    {form.is_commercial
                      ? "יתווסף [פרסומת עבורך] בסוף הנושא + Footer מלא (ח.פ, כתובת, לינק הסרה) + כותרות List-Unsubscribe. נשלח רק ללידים ב-consent_status=subscribed."
                      : "אישורי רכישה, חשבוניות, התראות. ללא [פרסומת עבורך], ללא Footer, נשלח גם למי שהסיר מנוי."}
                  </div>
                </div>
                <Switch
                  checked={!form.is_commercial}
                  onCheckedChange={(v) => set("is_commercial", !v)}
                  data-testid="commercial-toggle"
                  className="data-[state=checked]:bg-blue-600"
                />
              </div>
            </div>
          </Card>

          <Card title="קבוצה ולינק">
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-xl border border-stone-200">
              <div>
                <div className="font-[800] text-sm">קבוצה {form.group}</div>
                <div className="text-xs text-stone-500 leading-[1.6]">
                  {form.group === 1
                    ? "לינק אפיליאייט מקורי לפי הקמפיין שממנו הליד הגיע"
                    : "לינק בסיסי + ?sub1=<list_id> של האפיליאייט"}
                </div>
              </div>
              <Switch
                checked={form.group === 2}
                onCheckedChange={(v) => set("group", v ? 2 : 1)}
                data-testid="group-toggle"
                className="data-[state=checked]:bg-indigo-600"
              />
            </div>
            {form.group === 2 && (
              <Field label="לינק בסיסי (יתווסף אליו ?sub1=...)">
                <Input
                  value={form.generic_link}
                  onChange={(e) => set("generic_link", e.target.value)}
                  data-testid="form-generic-link"
                  dir="ltr"
                  placeholder="https://offer.example.com/landing"
                  className="h-11 ltr text-left"
                />
              </Field>
            )}
          </Card>

          {/* NEW: Campaign scope */}
          <Card title={<span className="inline-flex items-center gap-2"><Target className="w-4 h-4 text-brand-amber" />למי נשלח? (סינון קמפיין)</span>}>
            <div className={`p-3 rounded-xl border ${(form.campaign_ids || []).length === 0 ? "bg-stone-50 border-stone-200" : "bg-amber-50 border-amber-200"}`}>
              <div className="text-xs text-stone-700 leading-[1.65] mb-2.5">
                {(form.campaign_ids || []).length === 0 ? (
                  <>
                    <b>כל הלידים</b> בכל הקמפיינים יקבלו את המייל הזה (כולל הרשמות VOD ודף הנחיתה הראשי).
                    בחר קמפיינים ספציפיים כדי להגביל.
                  </>
                ) : (
                  <>
                    המייל יישלח <b>רק ללידים</b> שנרשמו ב-{(form.campaign_ids || []).length} הקמפיינים המסומנים למטה.
                    לידים בקמפיינים אחרים <b>לא</b> יקבלו את המייל הזה.
                  </>
                )}
              </div>
              {campaigns.length === 0 ? (
                <div className="text-xs text-stone-400">אין קמפיינים לבחירה.</div>
              ) : (
                <div className="max-h-52 overflow-y-auto border border-stone-200 rounded-lg divide-y divide-stone-100 bg-white" data-testid="campaign-scope-list">
                  {campaigns.map((c) => {
                    const checked = (form.campaign_ids || []).includes(c.id);
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-stone-50 select-none ${checked ? "bg-amber-50" : ""}`}
                        data-testid={`campaign-scope-item-${c.slug}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const cur = form.campaign_ids || [];
                            set("campaign_ids", cur.includes(c.id) ? cur.filter((x) => x !== c.id) : [...cur, c.id]);
                          }}
                          className="w-4 h-4 rounded border-stone-300 text-brand-amber focus:ring-brand-amber"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-stone-800 flex items-center gap-1.5">
                            {c.brand_name || c.slug}
                            {c.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-brand-amber text-white font-bold">ראשי</span>}
                            {c.slug === "vod" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-600 text-white font-bold">VOD</span>}
                          </div>
                          <div className="text-[11px] text-stone-500 ltr">{c.slug}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              {(form.campaign_ids || []).length > 0 && (
                <button
                  type="button"
                  onClick={() => set("campaign_ids", [])}
                  className="text-xs text-stone-500 hover:text-red-600 mt-2"
                  data-testid="campaign-scope-clear"
                >
                  נקה בחירה — שלח לכל הקמפיינים
                </button>
              )}
            </div>
          </Card>

          <Card title="טריגר השליחה">
            <Field label="סוג">
              <Select value={form.trigger_type} onValueChange={(v) => { set("trigger_type", v); set("trigger_value", {}); }}>
                <SelectTrigger className="h-11" data-testid="trigger-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRIGGER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>

            {form.trigger_type === "time_after_signup" && (
              <Field label="מתי לשלוח?">
                {(() => {
                  const tv = form.trigger_value || {};
                  const hasLegacy = tv.offset_days == null && tv.offset_minutes != null;
                  const days = tv.offset_days ?? 0;
                  const hour = tv.send_hour_local || "09:00";
                  return (
                    <div className="space-y-3">
                      <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-6">
                          <Label className="text-[11px] font-bold text-stone-600 mb-1 block">ימים מההרשמה</Label>
                          <Input
                            type="number"
                            min={0}
                            max={365}
                            value={days}
                            onChange={(e) => setForm((f) => ({
                              ...f,
                              trigger_value: { offset_days: Math.max(0, Number(e.target.value || 0)), send_hour_local: hour },
                            }))}
                            data-testid="trigger-days"
                            className="h-11"
                          />
                          <div className="text-[10px] text-stone-400 mt-1">0 = יום ההרשמה</div>
                        </div>
                        <div className="col-span-6">
                          <Label className="text-[11px] font-bold text-stone-600 mb-1 block">שעה ({tzLabel()})</Label>
                          <Input
                            type="time"
                            value={hour}
                            onChange={(e) => setForm((f) => ({
                              ...f,
                              trigger_value: { offset_days: days, send_hour_local: e.target.value || "09:00" },
                            }))}
                            data-testid="trigger-hour"
                            className="h-11 ltr text-left"
                          />
                        </div>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        {[
                          { l: "מיד", d: 0, h: "09:00" },
                          { l: "יום 1 · 09:00", d: 1, h: "09:00" },
                          { l: "יום 3 · 09:00", d: 3, h: "09:00" },
                          { l: "יום 5 · 09:00", d: 5, h: "09:00" },
                          { l: "יום 5 · 14:00", d: 5, h: "14:00" },
                          { l: "יום 7 · 09:00", d: 7, h: "09:00" },
                          { l: "יום 14 · 09:00", d: 14, h: "09:00" },
                        ].map((q) => (
                          <button
                            key={`${q.d}-${q.h}`}
                            type="button"
                            onClick={() => setForm((f) => ({
                              ...f,
                              trigger_value: { offset_days: q.d, send_hour_local: q.h },
                            }))}
                            className={`text-xs px-2.5 py-1 rounded-full transition ${
                              days === q.d && hour === q.h
                                ? "bg-brand-amber text-white font-bold"
                                : "bg-stone-100 hover:bg-stone-200 text-stone-700"
                            }`}
                            data-testid={`trigger-quick-${q.d}-${q.h}`}
                          >
                            {q.l}
                          </button>
                        ))}
                      </div>
                      <div className="text-xs text-stone-600 bg-stone-50 rounded-xl px-3 py-2 leading-[1.6]">
                        <b>{days === 0 ? "ביום ההרשמה" : `${days} ${days === 1 ? "יום" : "ימים"} לאחר ההרשמה`}</b>, בשעה <b className="ltr">{hour}</b> ({tzLabel()}).
                        <div className="text-[11px] text-stone-500 mt-0.5">
                          אם השעה כבר עברה ביום ההרשמה, המייל יידחה למחר באותה שעה.
                        </div>
                      </div>
                      {hasLegacy && (
                        <div className="text-[11px] text-orange-700 bg-orange-50 border border-orange-200 rounded-lg p-2 leading-[1.5]" data-testid="trigger-legacy-notice">
                          ⚠ תבנית ישנה זו הוגדרה ב-{tv.offset_minutes} דקות מההרשמה. עדכן ימים ושעה למעלה כדי לעבור למודל החדש.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Field>
            )}

            {form.trigger_type === "fixed_date" && (
              <Field label="תאריך + שעה">
                <Input
                  type="datetime-local"
                  value={utcISOToWallClock(form.trigger_value?.send_at)}
                  onChange={(e) => setTV("send_at", wallClockToUtcISO(e.target.value))}
                  data-testid="trigger-date"
                  className="h-11"
                />
                <p className="text-xs text-stone-500 mt-1">
                  השעה מתפרשת ב-<span className="font-bold text-brand-amber">{tzLabel()}</span>. המייל יישלח לכל הלידים הקיימים בזמן שיגדיר. לידים שיירשמו אחרי התאריך לא יקבלו.
                </p>
              </Field>
            )}

            {form.trigger_type === "after_email_opened" && (
              <>
                <Field label="אחרי שהמייל הזה נפתח">
                  <Select
                    value={form.trigger_value?.after_template_id || ""}
                    onValueChange={(v) => setTV("after_template_id", v)}
                  >
                    <SelectTrigger className="h-11" data-testid="trigger-after-template"><SelectValue placeholder="בחר מייל קודם" /></SelectTrigger>
                    <SelectContent>
                      {otherTemplates.length === 0 ? (
                        <div className="text-xs text-stone-500 p-3">צור template אחר תחילה</div>
                      ) : otherTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="עיכוב אחרי פתיחה (דקות)">
                  <Input
                    type="number"
                    min={0}
                    value={form.trigger_value?.delay_minutes || 0}
                    onChange={(e) => setTV("delay_minutes", Number(e.target.value))}
                    data-testid="trigger-delay"
                    className="h-11"
                  />
                </Field>
              </>
            )}
          </Card>

          <Card title="פעיל">
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600">{form.is_active ? "המייל יישלח לפי הטריגר" : "כבוי — לא יישלחו מיילים חדשים"}</span>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => set("is_active", v)}
                data-testid="active-toggle"
                className="data-[state=checked]:bg-emerald-600"
              />
            </div>
          </Card>
        </div>
      </div>

      {/* Preview modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" dir="rtl">
          <DialogHeader><DialogTitle>תצוגה מקדימה</DialogTitle></DialogHeader>
          <div className="text-xs text-stone-500 mb-2">נושא: <strong className="text-stone-900">{previewSubject}</strong></div>
          <div className="border border-stone-200 rounded-xl p-5 bg-white" dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </DialogContent>
      </Dialog>

      {/* Test send modal */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent dir="rtl">
          <DialogHeader><DialogTitle>שליחת מייל בדיקה</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-600 mb-3">המייל יישלח עם נתונים פיקטיביים ({"{{name}}"} = &quot;Test User&quot;). הנושא יוקדם ב-[TEST].</p>
          <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="test-email-input" placeholder="your@email.com" dir="ltr" className="h-11 ltr" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)}>בטל</Button>
            <Button onClick={sendTest} data-testid="send-test-confirm-btn" className="bg-brand-amber hover:bg-brand-amberHover text-white">שלח</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead-link picker */}
      <LeadLinkPickerDialog
        open={leadLinkOpen}
        onOpenChange={setLeadLinkOpen}
        destinations={leadLinkDestinations}
        shortLinks={shortLinks}
        onInsert={(html) => {
          insertAtCursor(html);
          setLeadLinkOpen(false);
        }}
      />

      {/* Named-link picker — per-campaign affiliate offers */}
      <NamedLinkPickerDialog
        open={namedLinkOpen}
        onOpenChange={setNamedLinkOpen}
        campaigns={campaigns}
        scopedCampaignIds={form.campaign_ids || []}
        onInsert={(html) => {
          insertAtCursor(html);
          setNamedLinkOpen(false);
        }}
      />
    </AdminLayout>
  );
}

function LeadLinkPickerDialog({ open, onOpenChange, destinations, shortLinks, onInsert }) {
  const [selectedKey, setSelectedKey] = useState("");
  const [label, setLabel] = useState("");
  const [style, setStyle] = useState("cta"); // "cta" (button) or "text" (inline link)

  // Merge both sources into one list. `kind: 'builtin' | 'short'` lets us
  // show the right placeholder format and preview URL per entry.
  const unifiedOptions = useMemo(() => {
    const builtins = (destinations || []).map((d) => ({
      kind: "builtin",
      key: d.key,
      label: d.label,
      placeholder: d.placeholder,
      preview_url: d.preview_url,
    }));
    const shorts = (shortLinks || []).map((s) => ({
      kind: "short",
      key: `short:${s.slug}`,
      label: s.title ? `${s.title} — /s/${s.slug}` : `/s/${s.slug}`,
      placeholder: `{{short_link:${s.slug}}}`,
      preview_url: s.long_url,
      slug: s.slug,
      long_url: s.long_url,
      click_count: s.click_count,
    }));
    return [...builtins, ...shorts];
  }, [destinations, shortLinks]);

  useEffect(() => {
    if (open && unifiedOptions.length && !selectedKey) {
      setSelectedKey(unifiedOptions[0].key);
      setLabel(unifiedOptions[0].label);
    }
  }, [open, unifiedOptions, selectedKey]);

  const selected = unifiedOptions.find((d) => d.key === selectedKey);

  const handleInsert = () => {
    if (!selected) return;
    const text = label.trim() || selected.label;
    if (style === "cta") {
      onInsert(
        `<p style="text-align:center;margin:24px 0"><a href="${selected.placeholder}" style="display:inline-block;background:#1c1917;color:#fff;padding:12px 24px;border-radius:999px;text-decoration:none;font-weight:bold">${text}</a></p>`
      );
    } else {
      onInsert(`<a href="${selected.placeholder}" style="color:#f59e0b;font-weight:bold">${text}</a>`);
    }
    setLabel("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="max-w-lg" data-testid="lead-link-dialog">
        <DialogHeader>
          <DialogTitle>הוסף לינק אישי (עם lead_id)</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-stone-600 leading-[1.6]">
            הלינק ייפתח עם המזהה של הליד שקיבל את המייל, כדי שהמערכת תזכה אותו כשהוא לוחץ. הבחירה שלך תהפוך לפלייסהולדר שיפתר בעת השליחה.
            לינקים מקוצרים מ-<code className="bg-stone-100 px-1 rounded ltr">/admin/short-links</code> יישלחו עם כל ה-sub1..sub10 של הליד שלוחצים.
          </p>

          <div>
            <Label className="text-sm font-bold text-stone-700 mb-1.5 block">יעד</Label>
            <Select value={selectedKey} onValueChange={(v) => {
              setSelectedKey(v);
              const d = unifiedOptions.find((x) => x.key === v);
              if (d) setLabel(d.label);
            }}>
              <SelectTrigger className="h-11" data-testid="lead-link-destination">
                <SelectValue placeholder="בחר יעד..." />
              </SelectTrigger>
              <SelectContent>
                {destinations && destinations.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-400">יעדים מובנים</div>
                    {destinations.map((d) => (
                      <SelectItem key={d.key} value={d.key} data-testid={`lead-link-option-${d.key}`}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </>
                )}
                {shortLinks && shortLinks.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-400 mt-1 border-t border-stone-100">קיצורי לינקים</div>
                    {shortLinks.map((s) => (
                      <SelectItem key={`short:${s.slug}`} value={`short:${s.slug}`} data-testid={`lead-link-option-short-${s.slug}`}>
                        <span className="ltr font-mono text-xs me-2 text-brand-amber">/s/{s.slug}</span>
                        <span>· {s.title || s.long_url.slice(0, 30)}</span>
                        {s.click_count > 0 && <span className="text-[10px] text-stone-400 ms-1">({s.click_count} קליקים)</span>}
                      </SelectItem>
                    ))}
                  </>
                )}
                {unifiedOptions.length === 0 && (
                  <div className="p-4 text-center text-xs text-stone-500">אין יעדים זמינים. צור קיצורי לינקים ב-/admin/short-links.</div>
                )}
              </SelectContent>
            </Select>
            {selected && (
              <div className="mt-1.5 space-y-0.5">
                <p className="text-xs text-stone-500 ltr text-left font-mono truncate" title={selected.preview_url}>
                  {selected.preview_url}
                </p>
                {selected.kind === "short" && (
                  <p className="text-[10px] text-brand-amber bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-block">
                    ✓ sub1..sub10 של הליד יצורפו ליעד הסופי בעת הלחיצה
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <Label className="text-sm font-bold text-stone-700 mb-1.5 block">טקסט הכפתור / הקישור</Label>
            <Input
              data-testid="lead-link-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="לחצו כדי להמשיך"
              className="h-11"
              maxLength={80}
            />
          </div>

          <div>
            <Label className="text-sm font-bold text-stone-700 mb-1.5 block">סגנון</Label>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="lead-link-style-cta"
                onClick={() => setStyle("cta")}
                className={`flex-1 p-3 rounded-xl border-2 transition text-sm font-bold ${
                  style === "cta"
                    ? "border-brand-amber bg-amber-50 text-brand-charcoal"
                    : "border-stone-200 hover:border-stone-300 text-stone-500"
                }`}
              >
                כפתור (CTA)
              </button>
              <button
                type="button"
                data-testid="lead-link-style-text"
                onClick={() => setStyle("text")}
                className={`flex-1 p-3 rounded-xl border-2 transition text-sm font-bold ${
                  style === "text"
                    ? "border-brand-amber bg-amber-50 text-brand-charcoal"
                    : "border-stone-200 hover:border-stone-300 text-stone-500"
                }`}
              >
                לינק טקסט
              </button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>ביטול</Button>
          <Button
            onClick={handleInsert}
            data-testid="lead-link-insert-btn"
            disabled={!selected}
            className="bg-brand-amber hover:bg-brand-amberHover text-white"
          >
            הכנס לתוכן
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-stone-200 space-y-3">
      <h3 className="font-[800] text-sm uppercase tracking-wider text-stone-500">{title}</h3>
      {children}
    </div>
  );
}

/**
 * NamedLinkPickerDialog — lets the editor insert a `{{link:<key>}}` placeholder
 * that resolves per-lead against `campaign.named_links` at send time.
 *
 * Grouping strategy:
 *  - Build a map { key → [{campaign_slug, campaign_name, label, url}, ...] } across
 *    all campaigns (respecting the scope selector if the template targets specific ones).
 *  - Show unique keys as choices. For each choice show which funnels have it defined
 *    (so the admin knows the fallback will kick in for campaigns missing the key).
 */
function NamedLinkPickerDialog({ open, onOpenChange, campaigns, scopedCampaignIds, onInsert }) {
  const [selectedKey, setSelectedKey] = useState("");
  const [label, setLabel] = useState("");
  const [style, setStyle] = useState("cta"); // "cta" | "text"

  // Restrict to scoped campaigns if the template is scoped; else consider all.
  const relevantCampaigns = useMemo(() => {
    if (!campaigns || !campaigns.length) return [];
    if (!scopedCampaignIds || !scopedCampaignIds.length) return campaigns;
    const set = new Set(scopedCampaignIds);
    return campaigns.filter((c) => set.has(c.id));
  }, [campaigns, scopedCampaignIds]);

  // Aggregate named_links keyed by `key` across relevant campaigns.
  const keyIndex = useMemo(() => {
    const map = new Map();
    for (const c of relevantCampaigns) {
      for (const link of (c.named_links || [])) {
        if (!link.key) continue;
        const arr = map.get(link.key) || [];
        arr.push({
          campaign_id: c.id,
          campaign_slug: c.slug,
          campaign_name: c.brand_name || c.slug,
          label: link.label || link.key,
          url: link.url,
        });
        map.set(link.key, arr);
      }
    }
    return map;
  }, [relevantCampaigns]);

  const keys = useMemo(() => Array.from(keyIndex.keys()).sort(), [keyIndex]);

  useEffect(() => {
    if (open && keys.length && !selectedKey) {
      setSelectedKey(keys[0]);
      const first = keyIndex.get(keys[0]);
      if (first && first.length) setLabel(first[0].label);
    }
    if (!open) {
      // Reset for next open
      setSelectedKey("");
      setLabel("");
    }
  }, [open, keys, keyIndex, selectedKey]);

  const selectedRows = selectedKey ? (keyIndex.get(selectedKey) || []) : [];
  const placeholder = selectedKey ? `{{link:${selectedKey}}}` : "";
  const coverage = selectedRows.length;
  const totalCampaigns = relevantCampaigns.length;

  const handleInsert = () => {
    if (!selectedKey) return;
    const text = (label || "").trim() || (selectedRows[0]?.label) || selectedKey;
    if (style === "cta") {
      onInsert(
        `<p style="text-align:center;margin:24px 0"><a href="${placeholder}" style="display:inline-block;background:#1c1917;color:#fff;padding:12px 24px;border-radius:999px;text-decoration:none;font-weight:bold">${text}</a></p>`
      );
    } else {
      onInsert(`<a href="${placeholder}" style="color:#f59e0b;font-weight:bold">${text}</a>`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="max-w-lg" data-testid="named-link-dialog">
        <DialogHeader>
          <DialogTitle>הוסף לינק אפיליאייט לפי שם</DialogTitle>
        </DialogHeader>

        {keys.length === 0 ? (
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-stone-700 leading-[1.7]">
            <b>אין עדיין לינקים בעלי שם.</b><br />
            כדי להשתמש בפיצ׳ר הזה, פתחו את עורך ה-funnel (Admin → קמפיינים), הוסיפו לפחות לינק אחד תחת <b>&quot;לינקי אפיליאייט נוספים&quot;</b> ותנו לו מזהה קצר (למשל <code className="bg-white/60 rounded px-1 ltr text-[10px]">main</code>).
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-stone-600 leading-[1.65]">
              בחרו מזהה לינק. בזמן שליחת המייל המערכת תחליף את ה-placeholder בכתובת שהוגדרה במזהה זה ב-funnel של הליד.
              אם ליד הגיע מ-funnel שאין בו את המזהה — יופנה ללינק ברירת המחדל של ה-funnel.
            </p>

            <div>
              <Label className="text-sm font-bold text-stone-700 mb-1.5 block">מזהה הלינק (KEY)</Label>
              <Select value={selectedKey} onValueChange={(v) => {
                setSelectedKey(v);
                const rows = keyIndex.get(v);
                if (rows && rows.length && !label.trim()) setLabel(rows[0].label);
              }}>
                <SelectTrigger className="h-11" data-testid="named-link-key-select">
                  <SelectValue placeholder="בחר מזהה..." />
                </SelectTrigger>
                <SelectContent>
                  {keys.map((k) => {
                    const rows = keyIndex.get(k) || [];
                    const primaryLabel = rows[0]?.label || k;
                    return (
                      <SelectItem key={k} value={k} data-testid={`named-link-option-${k}`}>
                        <span className="ltr font-mono text-xs me-2 text-brand-amber">{k}</span>
                        <span>· {primaryLabel} ({rows.length}/{totalCampaigns} funnels)</span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {selectedKey && (
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-stone-500 ltr text-left font-mono">
                    {placeholder}
                  </p>
                  <div className="text-[11px] text-stone-500 leading-[1.6]">
                    מוגדר ב-<b>{coverage}</b> מתוך <b>{totalCampaigns}</b> funnels:
                    {selectedRows.map((r) => (
                      <span key={r.campaign_id} className="inline-block bg-stone-100 rounded-full px-2 py-0.5 text-[10px] ms-1 mt-1">
                        {r.campaign_name}
                      </span>
                    ))}
                  </div>
                  {coverage < totalCampaigns && (
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 leading-[1.5]">
                      ⚠ לידים שמגיעים מ-funnels אחרים יופנו ללינק ברירת המחדל (Affiliate URL של ה-funnel).
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-bold text-stone-700 mb-1.5 block">טקסט הכפתור / הקישור</Label>
              <Input
                data-testid="named-link-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="להצעה המלאה →"
                className="h-11"
                maxLength={80}
              />
            </div>

            <div>
              <Label className="text-sm font-bold text-stone-700 mb-1.5 block">סגנון</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  data-testid="named-link-style-cta"
                  onClick={() => setStyle("cta")}
                  className={`flex-1 p-3 rounded-xl border-2 transition text-sm font-bold ${
                    style === "cta"
                      ? "border-brand-amber bg-amber-50 text-brand-charcoal"
                      : "border-stone-200 hover:border-stone-300 text-stone-500"
                  }`}
                >
                  כפתור (CTA)
                </button>
                <button
                  type="button"
                  data-testid="named-link-style-text"
                  onClick={() => setStyle("text")}
                  className={`flex-1 p-3 rounded-xl border-2 transition text-sm font-bold ${
                    style === "text"
                      ? "border-brand-amber bg-amber-50 text-brand-charcoal"
                      : "border-stone-200 hover:border-stone-300 text-stone-500"
                  }`}
                >
                  לינק טקסט
                </button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>ביטול</Button>
          <Button
            onClick={handleInsert}
            data-testid="named-link-insert-btn"
            disabled={!selectedKey}
            className="bg-brand-amber hover:bg-brand-amberHover text-white"
          >
            הכנס לתוכן
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold mb-1 block text-stone-700">{label}</Label>
      {children}
    </div>
  );
}

function ToolBtn({ onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-lg bg-white border border-stone-200 hover:border-brand-amber hover:text-brand-amber transition"
    >
      {children}
    </button>
  );
}
